<template>
  <NDropdown
    :options="options"
    placement="bottom-start"
    show
    trigger="manual"
    @click.stop="emit('click', $event)"
    @select="handleSelect"
  >
    <div />
  </NDropdown>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, h, inject, type ComputedRef } from 'vue'
import { NDropdown } from 'naive-ui'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'

import { IS_DEV_ORG, IS_LOCALHOST } from '@lib/constants/url.const'
import type { Folder, User } from '@openapi'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'
import useToast from '@lib/composables/useToast'
import useDeleteUsedCanvasesConfirmation from '@lib/composables/useDeleteUsedCanvasesConfirmation.ts'
import type { ViewCompactFileFolder } from '@lib/types/files'
import useAppStore, { type StateSelectedItem } from '@browser/stores/app'
import useApiStore from '@browser/stores/api'
import useAppEmit from '@browser/composables/useAppEmit'
import useFileSmartFolderStore from '@browser/stores/fileSmartFolderStore'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { ActionKeys } from '@browser/types/actionKeys'

const emit = defineEmits<{
  click: [e: MouseEvent]
  'open-details-panel': [item: ViewCompactFileFolder]
  'toggle-favorite': [item: ViewCompactFileFolder]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const myUser = inject<ComputedRef<User | null>>(
  'myUser',
  computed(() => null),
)
const { t } = useI18n()
const themeVars = useThemeVars()
const confirmation = useConfirmation()
const { toast } = useToast()
const { appEmit } = useAppEmit()
const isItemInSharebox = inject<(id: string) => boolean>('isItemInSharebox', () => false)

const { getDeleteConfirmationConfig } = useDeleteUsedCanvasesConfirmation()

interface Props {
  item: ViewCompactFileFolder
}

const props = defineProps<Props>()

const {
  setCurrentState,
  appName,
  resetSelectedItems,
  toggleItemSelection,
  inlineEditingItem,
  selectedView,
  enabledFavorites,
} = useAppStore()
const { state: apiState, actions: apiActions } = useApiStore()
const smartFolderStore = useFileSmartFolderStore()

const renderIcon = (icon: string, faType?: 'far' | 'fas') => () =>
  h(CIcon, {
    faType,
    icon,
  })

const options: ComputedRef<DropdownMixedOption[]> = computed(() => {
  const options: DropdownMixedOption[] = []
  const errorStyleProps = {
    style: {
      '--n-prefix-color': themeVars.value.accent,
      '--n-option-text-color-hover': themeVars.value.accent,
    },
  }

  if (props.item.type === 'file') {
    options.push({
      label: t('canvasUI.browserApp.contextMenu.view'),
      icon: renderIcon('eye'),
      key: ActionKeys.ViewFile,
    })

    if (!isPitcherWkWebView) {
      options.push({
        label: t('canvasUI.browserApp.contextMenu.viewNewTab'),
        icon: renderIcon('browsers'),
        key: ActionKeys.ViewFileNewTab,
      })
    }

    if (appName.value === 'admin') {
      options.push({
        label: t('canvasUI.browserApp.contextMenu.viewFileDetails'),
        icon: renderIcon('file-circle-info'),
        key: ActionKeys.ViewFileDetails,
      })
    }
  }

  if (props.item.type === 'folder') {
    options.push({
      label: t('canvasUI.browserApp.contextMenu.viewFolder'),
      icon: renderIcon('folder-open'),
      key: ActionKeys.ViewFolder,
    })

    if (!isPitcherWkWebView) {
      options.push({
        label: t('canvasUI.browserApp.contextMenu.viewFolderNewTab'),
        icon: renderIcon('browsers'),
        key: ActionKeys.ViewFolderNewTab,
      })
    }

    if (appName.value === 'admin') {
      options.push({
        label: t('canvasUI.browserApp.contextMenu.viewFolderDetails'),
        icon: renderIcon('folder-gear'),
        key: ActionKeys.ViewFolderDetails,
      })
    }
  }

  options.push({
    type: 'divider',
    key: 'd1',
  })

  if (props.item.type === 'file' && appName.value === 'impact' && !!launchDarkly.value.enable_sharebox) {
    const isBulkFilteringEnabled = !!launchDarkly.value.enable_sharebox_bulk_filtering
    const isInSharebox = isItemInSharebox(props.item.id)
    const isDisabled = isBulkFilteringEnabled
      ? props.item?.permissions?.can_share !== true || isInSharebox
      : props.item?.permissions?.can_share !== true
    options.push({
      label: isInSharebox
        ? t('canvasUI.browserApp.contextMenu.alreadyInSharebox')
        : t('canvasUI.browserApp.contextMenu.addToSharebox'),
      icon: renderIcon('share-from-square'),
      key: ActionKeys.AddToSharebox,
      disabled: isDisabled,
    })
  }

  if (enabledFavorites.value) {
    options.push(
      props.item.favoriteId
        ? {
            label: t('canvasUI.browserApp.contextMenu.removeFromFavorites'),
            icon: renderIcon('heart', 'fas'),
            key: ActionKeys.RemoveFromFavorites,
            props: errorStyleProps,
          }
        : {
            label: t('canvasUI.browserApp.contextMenu.addtoFavorites'),
            icon: renderIcon('heart'),
            key: ActionKeys.AddToFavorites,
          },
    )
  }

  options.push({
    label: t('canvasUI.browserApp.contextMenu.openFileDetailsPanel'),
    icon: renderIcon('info-circle'),
    key: ActionKeys.OpenDetailsPanel,
  })

  if (appName.value === 'admin' && !smartFolderStore.isSmartFoldersView.value) {
    options.push({
      label: t('canvasUI.browserApp.contextMenu.move'),
      icon: renderIcon('folder-arrow-up'),
      key: ActionKeys.Move,
    })
  }

  if (props.item.type === 'file' && appName.value === 'admin') {
    ;(IS_DEV_ORG || IS_LOCALHOST) &&
      options.push(
        {
          type: 'divider',
          key: 'd2',
        },
        {
          label: t('canvasUI.browserApp.contextMenu.generateKeypoints'),
          icon: renderIcon('cat-space'),
          key: ActionKeys.GenerateKeypoints,
        },
      )
    options.push(
      {
        type: 'divider',
        key: 'd3',
      },
      ...(selectedView.value === 'table'
        ? [
            {
              label: t('canvasUI.browserApp.contextMenu.renameFile'),
              icon: renderIcon('pen-line'),
              key: ActionKeys.RenameFile,
            },
            {
              label: t('canvasUI.browserApp.contextMenu.editFileTags'),
              icon: renderIcon('tags'),
              key: ActionKeys.EditFileTags,
            },
          ]
        : []),
      {
        label: t('canvasUI.browserApp.contextMenu.deleteFile'),
        icon: renderIcon('trash'),
        key: ActionKeys.DeleteFile,
        props: errorStyleProps,
      },
    )
  } else if (
    props.item.type === 'file' &&
    appName.value !== 'admin' &&
    launchDarkly.value?.allow_rep_file_upload &&
    myUser.value?.id &&
    props.item.owned_by &&
    myUser.value.id === props.item.owned_by.id
  ) {
    options.push(
      {
        type: 'divider',
        key: 'd3',
      },
      {
        label: t('canvasUI.browserApp.contextMenu.deleteFile'),
        icon: renderIcon('trash'),
        key: ActionKeys.DeleteFile,
        props: errorStyleProps,
      },
    )
  } else if (props.item.type === 'folder' && appName.value === 'admin') {
    options.push({
      label: t('canvasUI.browserApp.contextMenu.renameFolder'),
      icon: renderIcon('pen-line'),
      key: ActionKeys.RenameFolder,
    })
    options.push({
      label: t('canvasUI.browserApp.contextMenu.deleteFolder'),
      icon: renderIcon('trash'),
      key: ActionKeys.DeleteFolder,
      props: errorStyleProps,
    })
  }

  return options
})

async function handleSelect(key: string) {
  switch (key as ActionKeys) {
    case ActionKeys.AddToFavorites:
    case ActionKeys.RemoveFromFavorites:
      emit('toggle-favorite', props.item)
      break
    case ActionKeys.OpenDetailsPanel:
      emit('open-details-panel', props.item)
      break
    case ActionKeys.DeleteFolder:
      if (apiState.folderDeleter) {
        const config = await getDeleteConfirmationConfig({
          item: props.item,
          deleteFunction: apiState.folderDeleter,
          action: ActionKeys.DeleteFolder,
          onAfterDelete: () => {
            if (smartFolderStore.isSmartFoldersView.value) {
              smartFolderStore.actions.fetchData()
            }
          },
        })
        confirmation.danger(config)
      }
      break
    case ActionKeys.DeleteFile:
      if (apiState.fileDeleter) {
        const config = await getDeleteConfirmationConfig({
          item: props.item,
          deleteFunction: apiState.fileDeleter,
          action: ActionKeys.DeleteFile,
          onAfterDelete: () => {
            if (smartFolderStore.isSmartFoldersView.value) {
              smartFolderStore.actions.fetchData()
            }
          },
        })
        confirmation.danger(config)
      }
      break

    case ActionKeys.EditFileTags:
      inlineEditingItem.value = { id: props.item.id, prop: 'tags' }
      break
    case ActionKeys.RenameFile:
      inlineEditingItem.value = { id: props.item.id, prop: 'name' }
      break
    case ActionKeys.RenameFolder:
      if (selectedView.value === 'table') {
        inlineEditingItem.value = { id: props.item.id, prop: 'name' }
      } else if (selectedView.value === 'grid') {
        setCurrentState('update-folder', props.item.id)
      }
      break
    case ActionKeys.Move:
      resetSelectedItems()
      toggleItemSelection(props.item as StateSelectedItem)
      setCurrentState('batch-move')
      break
    case ActionKeys.GenerateKeypoints:
      appEmit('generateKeypoints', props.item.id)
      break
    case ActionKeys.ViewFileDetails:
      appEmit('viewFileDetails', props.item.id)
      break
    case ActionKeys.ViewFolderDetails:
      appEmit('viewFolderDetails', props.item.id)
      break
    case ActionKeys.AddToSharebox:
      appEmit('addToSharebox', {
        id: props.item.id,
        fileId: props.item.id,
        name: props.item.name,
        type: 'file',
        thumbnailUrl: props.item.thumbnail_url,
      })
      toast.success(t('canvasUI.browserApp.contextMenu.contentAddedToSharebox'))
      break
    case ActionKeys.ViewFile:
      apiState.fileOpener?.({ fileId: props.item.id })
      break
    case ActionKeys.ViewFileNewTab:
      apiState.fileOpener?.({ fileId: props.item.id, newTab: true })
      break
    case ActionKeys.ViewFolder:
      handleViewFolder(props.item.id)
      break
    case ActionKeys.ViewFolderNewTab:
      handleViewFolder(props.item.id, true)
      break
  }
}

function handleViewFolder(folderId: Folder['id'], newTab = false) {
  appEmit('onSectionChange', { section: 'all', folderId: folderId, newTab: newTab })
  if (!newTab) {
    apiActions.setSearchQuery('')
  }
}
</script>
